import type { Quiz, QuizCategory } from '@efdigital/ll-addon-data';
import { DataTypeQuiz } from '@efdigital/ll-addon-data/data-types';
import { textColorStyle } from '@efdigital/ll-admin-v2/lib/color';
import { generateRandomSlug } from '@efdigital/ll-admin-v2/lib/random-slug';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Type } from '@warp-drive/core/types/symbols';

import { DragToSort } from '../drag-to-sort.gts';
import { type EditRecordSignature } from '../edit-record.gts';
import { EditRecord } from '../edit-record.gts';

export class EditQuizCategories extends Component<EditRecordSignature<Quiz>> {
  @service declare store: Store;
  @tracked selectedIndex = -1;

  onEdit = () => {
    this.selectedIndex = -1;
  };

  setSelectedIndex = (index: number) => {
    this.selectedIndex = index;
  };

  removeCategoryAt = (categories: QuizCategory[], index: number) => {
    const copy = [...categories];
    copy.splice(index, 1);
    categories.splice(0, categories.length, ...copy.map((c) => ({ ...c })));
  };

  newCategory = () => {
    const category: QuizCategory = {
      [Type]: DataTypeQuiz.QuizCategory,
      cid: generateRandomSlug(),
      title: '',
      parent: '',
      colour: '#A15D9A',
      sd: 0,
      ic: 0,
      mappings: [],
    };
    this.args.item.categories = [
      ...(this.args.item.categories || []),
      category,
    ];
    this.selectedIndex = this.args.item.categories.length - 1;
  };

  setCategoryTitle = (category: QuizCategory, event: Event) => {
    const target = event.target as HTMLInputElement;
    category.title = target.value;
  };

  setCategoryColour = (category: QuizCategory, event: Event) => {
    const target = event.target as HTMLInputElement;
    category.colour = target.value;
  };

  isEditing = (index: number) => {
    return this.selectedIndex === index;
  };

  updateCategories = (categories: QuizCategory[]) => {
    this.args.item.categories = [...categories];
  };

  <template>
    <EditRecord
      @editLabel="Edit Categories"
      @item={{@item}}
      @context={{@context}}
      @onEdit={{this.onEdit}}
      ...attributes
    >
      <:form as |uniqueId isDisabled isReadOnly|>
        {{#if isReadOnly}}
          <h3 class="gud-eyebrow">Scoring Categories</h3>
          <p class="form-text">
            <span
              class="glyphicon glyphicon-info-sign"
              aria-hidden="true"
            ></span>
            Leave this section blank to use default scoring. To enable scoring
            in different categories (e.g. for psychometrics), add categories
            here.
          </p>
          <div class="list-group mb-3">
            {{#each @item.categories as |category|}}
              <div class="list-group-item">
                <iconify-icon
                  inline
                  icon="ph:sigma-fill"
                  style={{textColorStyle category.colour}}
                />
                {{#if category.title}}
                  {{category.title}}
                {{else}}
                  (Untitled Category)
                {{/if}}
              </div>
            {{/each}}
          </div>
        {{else}}
          <fieldset disabled={{isDisabled}} class="mb-0">
            <legend class="gud-eyebrow">Scoring Categories</legend>
            <p class="form-text">
              <span
                class="glyphicon glyphicon-info-sign"
                aria-hidden="true"
              ></span>
              Leave this section blank to use default scoring. To enable scoring
              in different categories (e.g. for psychometrics), add categories
              here.
            </p>
            <div class="list-group mb-3 gap-1">
              <DragToSort
                @items={{@item.categories}}
                @updateItems={{this.updateCategories}}
              >
                <:item as |dropZone draggable category index|>
                  {{#let (this.isEditing index) as |isEditing|}}
                    <div class="{{if isEditing 'shadow rounded-bottom-1'}}">
                      <div class="input-group" {{dropZone index}}>
                        <span
                          class="input-group-text justify-content-center fw-bold drag-handle w-3rem fs-4
                            {{if isEditing 'rounded-bottom-0'}}"
                          {{draggable category index}}
                          title="Drag to reorder"
                        >
                          <span class="visually-hidden">Category</span>
                          <iconify-icon
                            inline
                            icon="ph:sigma-fill"
                            style={{textColorStyle category.colour}}
                          />
                        </span>

                        {{#if isEditing}}
                          <button
                            aria-label="Stop editing"
                            class="btn btn-primary rounded-0 fs-4 px-3 py-0"
                            type="button"
                            {{on "click" (fn this.setSelectedIndex -1)}}
                          ><iconify-icon inline icon="ph:check-bold" /></button>
                        {{else}}
                          <button
                            aria-label="Edit category"
                            class="btn btn-outline-primary rounded-0 fs-4 px-3 py-0"
                            type="button"
                            {{on "click" (fn this.setSelectedIndex index)}}
                          ><iconify-icon
                              inline
                              icon="ph:pencil-ruler"
                            /></button>
                        {{/if}}

                        <input
                          type="text"
                          class="form-control list-group-item"
                          value={{category.title}}
                          aria-label="Question title"
                          required
                          {{on "change" (fn this.setCategoryTitle category)}}
                        />
                        <button
                          aria-label="Remove category"
                          class="btn btn-outline-primary rounded-end-1
                            {{if isEditing 'rounded-bottom-0'}}"
                          type="button"
                          {{on
                            "click"
                            (fn this.removeCategoryAt @item.categories index)
                          }}
                        ><iconify-icon inline icon="ph:trash-bold" /></button>
                        {{#if isEditing}}
                          <div
                            class="card rounded-top-0 border-top-0 bg-primary-subtle ll-quiz-category-editor w-100"
                          >
                            <div class="card-body">
                              <div class="form-text mb-3">This color appears in
                                charts when users view their results.</div>
                              <label for="{{uniqueId}}-colour">Colour</label>
                              <input
                                type="color"
                                value={{category.colour}}
                                id="{{uniqueId}}-colour"
                                class="form-control"
                                {{on
                                  "change"
                                  (fn this.setCategoryColour category)
                                }}
                              />
                            </div>
                          </div>
                        {{/if}}
                      </div>
                    </div>
                  {{/let}}
                </:item>
              </DragToSort>
            </div>
          </fieldset>
        {{/if}}
      </:form>
      <:buttons as |uniqueId isReadOnly|>
        {{#unless isReadOnly}}
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            {{on "click" this.newCategory}}
          >Add Category</button>
        {{/unless}}
      </:buttons>
    </EditRecord>
  </template>
}
